/**
 * MIME-prefix classification for attachments. One home for the
 * `fileType.startsWith("image/")` / `"video/"` convention used across the chat
 * UI, so the viewer, bubbles, nav list, prefetch and auto-download all agree on
 * what counts as an image/video/media item.
 *
 * (Scoped to the attachment-preview code for now; the pre-existing open-coded
 * sites elsewhere can migrate onto these incrementally.)
 */
export const isImageType = (fileType?: string): boolean =>
  !!fileType?.startsWith("image/");

export const isVideoType = (fileType?: string): boolean =>
  !!fileType?.startsWith("video/");

/** Image or video — the content types that share the media carousel. */
export const isMediaType = (fileType?: string): boolean =>
  isImageType(fileType) || isVideoType(fileType);

/**
 * The granularity SIZE LIMITS are keyed on. Deliberately finer than
 * `getAttachmentCategory` in lib/utils, which collapses image and video into
 * "media" — they have different caps, so limits cannot share a bucket.
 *
 * Voice is absent on purpose: it is capped by DURATION
 * (`PLAN_LIMITS.voiceNoteDuration`), never by bytes, and never reaches the size
 * checks.
 */
export type AttachmentKind = "pdf" | "image" | "video";

/**
 * Which size limit applies to this MIME type.
 *
 * Total for anything the app actually accepts: `ALLOWED_DOC_TYPES` is PDF-only,
 * so a non-image, non-video attachment is a PDF. Anything unrecognised is
 * treated as a document, which is the conservative choice — documents are the
 * one kind whose limit also varies per plan in Architect chats.
 */
export const attachmentKind = (fileType?: string): AttachmentKind => {
  if (isImageType(fileType)) {
    return "image";
  }
  if (isVideoType(fileType)) {
    return "video";
  }
  return "pdf";
};
